import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '../lib/database.types';
import { generateComparativeAnalysis } from './comparativeAnalysis';
import { SavedValuation, ValuationData } from '../types/valuation';

export interface ValuationFormData {
  propertyId: string;
  propertyName: string;
  propertyAddress: string;
  propertyCity: string;
  propertyState: string;
  propertyCounty: string;
  fuelBrand: string;
  siteSize: number;
  buildingSize: number;
  yearBuilt: number;
  fuelVolume: number;
  cstoreSales: number;
  ebitda: number;
  trafficCount: number;
  location: string;
  propertyType: string;
  ownership: string;
  brandAffiliation: string;
  [key: string]: any;
}

export interface SaveValuationOptions {
  propertyId: string;
  userId: string;
  name: string;
  data: ValuationData;
}

/**
 * Submits a property valuation and generates comparative analysis
 */
export async function submitValuation(
  supabase: SupabaseClient<Database>,
  userId: string,
  formData: ValuationFormData
) {
  try {
    // First, insert or update the valuation record
    const { data: valuationData, error: valuationError } = await supabase
      .from('valuations')
      .insert({
        property_id: formData.propertyId,
        user_id: userId,
        location: formData.location || 'Unknown Location',
        property_type: formData.propertyType || 'gas_station',
        ownership: formData.ownership || 'owned',
        brand_affiliation: normalizeBrandAffiliation(formData.brandAffiliation || 'unbranded'),
        estimated_value: calculateEstimatedValue(formData),
        confidence_score: 0.85, // Default confidence score
        monthly_fuel_revenue: formData.fuelVolume * 0.25, // Example calculation
        store_revenue: formData.cstoreSales / 12, // Convert annual to monthly
        operating_expenses: formData.ebitda * 0.4, // Example calculation
        factors: {
          site_size: formData.siteSize,
          building_size: formData.buildingSize,
          year_built: formData.yearBuilt,
          traffic_count: formData.trafficCount
        }
      })
      .select('id')
      .single();

    if (valuationError) {
      throw new Error(`Failed to save valuation: ${valuationError.message}`);
    }

    // Extract metrics for comparative analysis
    const valuationMetrics = {
      fuelVolume: formData.fuelVolume,
      storeRevenue: formData.cstoreSales,
      operatingExpenses: formData.ebitda, // Using EBITDA as operating expenses proxy
      siteSize: formData.siteSize,
      trafficCount: formData.trafficCount,
      yearBuilt: formData.yearBuilt
    };

    // Property data for filtering comparisons
    const propertyData = {
      fuelBrand: formData.fuelBrand,
      county: formData.propertyCounty
    };

    // Generate comparative analysis
    const analysis = await generateComparativeAnalysis(
      userId,
      valuationData.id,
      formData.propertyId,
      propertyData,
      valuationMetrics
    );

    return {
      success: true,
      valuationId: valuationData.id,
      analysis
    };
  } catch (error) {
    console.error('Error submitting valuation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to submit valuation'
    };
  }
}

/**
 * Simple function to calculate estimated property value
 * This would be replaced with your actual valuation algorithm
 */
function calculateEstimatedValue(formData: ValuationFormData): number {
  // This is a placeholder formula - replace with your actual valuation model
  const baseValue = 500000;
  const volumeMultiplier = formData.fuelVolume * 0.5;
  const salesMultiplier = formData.cstoreSales * 1.2;
  const sizeMultiplier = formData.siteSize * 100;
  
  return baseValue + volumeMultiplier + salesMultiplier + sizeMultiplier;
}

/**
 * Saves a complete valuation with all detailed data
 * @param supabase Supabase client instance
 * @param options Options containing user ID, property ID, name and valuation data
 * @returns The saved valuation with ID and timestamp or an error
 */
export async function saveValuationData(
  supabase: SupabaseClient<Database> | { client: SupabaseClient<Database> },
  options: SaveValuationOptions
): Promise<{ success: boolean; data?: SavedValuation; error?: string }> {
  try {
    const { propertyId, userId, name, data } = options;
    
    console.log('💾 Starting saveValuationData with:', { propertyId, userId, name });
    console.log('📋 Input data structure:', Object.keys(data));
    console.log('📋 Full input data:', data);
    
    // Calculate estimated value and confidence score
    const estimatedValue = calculateValueFromDetailedData(data);
    const confidenceScore = calculateConfidenceScore(data);
    
    console.log('🧮 Calculated values:', { estimatedValue, confidenceScore });
    
    // Extract specific fields from the valuation data - with logging
    console.log('🔍 Attempting to extract fields from data...');
    const assets = data.assets;
    const propertySize = data.propertySize;
    const lotSize = data.lotSize;
    const annualGallons = data.annualGallons;
    const storeRevenue = data.storeRevenue;
    const operatingExpenses = data.operatingExpenses;
    const carWashRevenue = data.carWashRevenue;
    const additionalServices = data.additionalServices;
    const yearBuilt = data.yearBuilt;
    const trafficCount = data.trafficCount;
    const brandName = data.brandName;
    const storeBrand = data.storeBrand;
    const location = data.location;
    const totalGrossProfit = data.totalGrossProfit;
    
    console.log('📊 Extracted field values:', {
      assets: !!assets,
      propertySize,
      lotSize,
      annualGallons,
      storeRevenue,
      operatingExpenses,
      carWashRevenue,
      additionalServices,
      yearBuilt,
      trafficCount,
      brandName,
      storeBrand,
      location,
      totalGrossProfit
    });
    
    // Get the actual client - handle both direct client and interface with client property
    const client = 'from' in supabase ? supabase : supabase.client;
    
    console.log('🗄️ Preparing database insert...');
    const insertPayload = {
      property_id: propertyId,
      user_id: userId,
      location: location || 'Unknown Location',
      property_type: data.propertyType || 'gas_station',
      ownership: data.ownership || 'owned',
      brand_affiliation: normalizeBrandAffiliation(data.brandAffiliation || 'unbranded'),
      estimated_value: estimatedValue,
      confidence_score: confidenceScore,
      monthly_fuel_revenue: annualGallons ? annualGallons / 12 : null,
      store_revenue: storeRevenue,
      operating_expenses: operatingExpenses || null,
      car_wash_revenue: carWashRevenue || null,
      additional_services: additionalServices || null,
      factors: {
        site_size: propertySize,
        lot_size: lotSize,
        year_built: yearBuilt,
        traffic_count: trafficCount,
        fuel_brand: brandName,
        store_brand: storeBrand,
        // Include new building types if present
        kiosk_size: assets?.Kiosk?.size || null,
        kiosk_condition: assets?.Kiosk?.condition || null,
        food_booth_size: assets?.Food_Booth?.size || null,
        food_booth_condition: assets?.Food_Booth?.condition || null,
        // Store the full valuation data for future reference
        full_valuation_data: data
      }
    };
    
    console.log('📤 Insert payload:', insertPayload);
    
    // First insert the basic valuation record
    const { data: valuationRecord, error: valuationError } = await client
      .from('valuations')
      .insert(insertPayload)
      .select('id, created_at')
      .single();

    console.log('✅ Database insert result:', { valuationRecord, valuationError });

    if (valuationError) {
      console.error('❌ Database insert failed:', valuationError);
      throw new Error(`Failed to save valuation: ${valuationError.message}`);
    }

    // Generate comparative analysis based on the valuation
    try {
      await generateComparativeAnalysis(
        userId,
        valuationRecord.id,
        propertyId,
        {
          fuelBrand: brandName,
          county: location?.split(',')[1]?.trim() || undefined
        },
        {
          fuelVolume: annualGallons || 0,
          storeRevenue: storeRevenue || 0,
          operatingExpenses: operatingExpenses || 0,
          siteSize: propertySize || 0,
          trafficCount: trafficCount || 0,
          yearBuilt: yearBuilt || new Date().getFullYear()
        }
      );
    } catch (analysisError) {
      console.error('Error generating comparative analysis:', analysisError);
    }

    // Create the saved valuation object with id, name, data, and timestamp
    const savedValuation: SavedValuation = {
      id: valuationRecord.id,
      name,
      data,
      lastModified: valuationRecord.created_at
    };

    return {
      success: true,
      data: savedValuation
    };
  } catch (error) {
    console.error('Error saving valuation data:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save valuation data'
    };
  }
}

/**
 * Retrieves saved valuations for a specific user
 * @param supabase Supabase client or interface
 * @param userId The user ID to retrieve valuations for
 * @returns Array of saved valuations or an error
 */
export async function retrieveUserValuations(
  supabase: SupabaseClient<Database> | { client: SupabaseClient<Database> },
  userId: string
): Promise<{ success: boolean; data?: SavedValuation[]; error?: string }> {
  try {
    // Get the actual client - handle both direct client and interface with client property
    const client = 'from' in supabase ? supabase : supabase.client;
    
    console.log('🔍 Retrieving valuations for user:', userId);
    
    // Query valuations for the given user, excluding hidden valuations
    const { data, error } = await client
      .from('valuations')
      .select('id, property_id, created_at, estimated_value, factors, location')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    console.log('📊 Database query result:', { data, error, count: data?.length });

    if (error) {
      console.error('❌ Database error:', error);
      throw new Error(`Failed to fetch valuations: ${error.message}`);
    }

    if (!data || data.length === 0) {
      console.log('📭 No valuations found for user');
      return { success: true, data: [] };
    }

    // Map database records to SavedValuation format
    const savedValuations: SavedValuation[] = data.map((record: any) => {
      console.log('🔄 Processing valuation record:', record.id);
      console.log('📋 Full record data:', record);
      
      // Extract full valuation data if available, or create a minimal version
      const valuationData: ValuationData = 
        record.factors?.full_valuation_data || 
        createMinimalValuationData(record);
      
      const savedValuation = {
        id: record.id,
        name: record.location || `Valuation for Property ${record.property_id}`,
        lastModified: record.created_at,
        data: valuationData,
        hidden: record.factors?.hidden || false
      };
      
      console.log('✅ Mapped valuation:', { 
        id: savedValuation.id, 
        name: savedValuation.name, 
        location: record.location,
        hidden: savedValuation.hidden 
      });
      
      return savedValuation;
    });

    console.log('🎯 Final result:', { count: savedValuations.length, valuations: savedValuations.map(v => ({ id: v.id, name: v.name })) });

    return {
      success: true,
      data: savedValuations
    };
  } catch (error) {
    console.error('Error retrieving valuations:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to retrieve valuations'
    };
  }
}

/**
 * Deletes a valuation by ID
 * @param supabase Supabase client instance
 * @param valuationId The valuation ID to delete
 * @param userId The user ID who owns the valuation (for security verification)
 * @returns Success status and message
 */
export async function deleteValuation(
  supabase: SupabaseClient<Database>,
  valuationId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Verify the user owns this valuation before deleting
    const { data: valuationCheck, error: checkError } = await supabase
      .from('valuations')
      .select('id')
      .eq('id', valuationId)
      .eq('user_id', userId)
      .single();
    
    if (checkError || !valuationCheck) {
      return { 
        success: false, 
        error: 'Valuation not found or you do not have permission to delete it' 
      };
    }
    
    // Delete the valuation
    const { error } = await supabase
      .from('valuations')
      .delete()
      .eq('id', valuationId)
      .eq('user_id', userId); // Double check to ensure user owns it
    
    if (error) {
      throw new Error(`Failed to delete valuation: ${error.message}`);
    }
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Error deleting valuation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete valuation'
    };
  }
}

export async function retrieveValuationById(
  supabase: SupabaseClient<Database> | { client: SupabaseClient<Database> },
  valuationId: string
): Promise<{ success: boolean; data?: SavedValuation; error?: string }> {
  try {
    // Get the actual client - handle both direct client and interface with client property
    const client = 'from' in supabase ? supabase : supabase.client;
    
    // Query the specific valuation
    const { data, error } = await client
      .from('valuations')
      .select('id, property_id, created_at, estimated_value, factors')
      .eq('id', valuationId)
      .single();

    if (error) {
      throw new Error(`Failed to fetch valuation: ${error.message}`);
    }

    if (!data) {
      throw new Error(`Valuation with ID ${valuationId} not found`);
    }

    // Extract full valuation data if available, or create a minimal version
    const valuationData: ValuationData = 
      data.factors?.full_valuation_data || 
      createMinimalValuationData(data);
    
    // Create the saved valuation object
    const savedValuation: SavedValuation = {
      id: data.id,
      name: `Valuation for Property ${data.property_id}`,
      lastModified: data.created_at,
      data: valuationData
    };

    return {
      success: true,
      data: savedValuation
    };
  } catch (error) {
    console.error('Error retrieving valuation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to retrieve valuation'
    };
  }
}

/**
 * Hides a valuation from the user's view (soft delete)
 * @param supabase Supabase client or interface
 * @param valuationId The ID of the valuation to hide
 * @param userId The user ID who owns the valuation (for security verification)
 * @returns Success status and message
 */
export async function hideValuation(
  supabase: SupabaseClient<Database> | { client: SupabaseClient<Database> },
  valuationId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get the actual client - handle both direct client and interface with client property
    const client = 'from' in supabase ? supabase : supabase.client;
    
    // Verify the user owns this valuation before hiding
    const { data: valuationCheck, error: checkError } = await client
      .from('valuations')
      .select('id, factors')
      .eq('id', valuationId)
      .eq('user_id', userId)
      .single();
    
    if (checkError || !valuationCheck) {
      return { 
        success: false, 
        error: 'Valuation not found or you do not have permission to hide it' 
      };
    }
    
    // Update the factors to include hidden flag
    const updatedFactors = {
      ...valuationCheck.factors,
      hidden: true
    };
    
    // Update the valuation to mark it as hidden
    const { error } = await client
      .from('valuations')
      .update({ factors: updatedFactors })
      .eq('id', valuationId)
      .eq('user_id', userId); // Double check to ensure user owns it
    
    if (error) {
      throw new Error(`Failed to hide valuation: ${error.message}`);
    }
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Error hiding valuation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to hide valuation'
    };
  }
}

/**
 * Helper function to calculate building size from assets
 */
function getBuildingSize(assets: ValuationData['assets']): number {
  let totalSize = 0;
  
  // Add sizes of all building-related assets
  if (assets.Convenience_Store?.size) totalSize += assets.Convenience_Store.size;
  if (assets.Service_Station?.size) totalSize += assets.Service_Station.size;
  if (assets.Kiosk?.size) totalSize += assets.Kiosk.size;
  if (assets.Food_Booth?.size) totalSize += assets.Food_Booth.size;
  
  return totalSize;
}

/**
 * Helper function to calculate estimated value from detailed valuation data
 */
function calculateValueFromDetailedData(data: ValuationData): number {
  // First, check if we have API response data with the actual ML model results
  if (data.apiResponse?.result?.total_station_value) {
    console.log('🎯 Using API response total station value:', data.apiResponse.result.total_station_value);
    return Math.round(data.apiResponse.result.total_station_value);
  }
  
  // Fallback to heuristic calculation if no API response
  console.log('⚠️ No API response found, using heuristic calculation');
  let value = 500000; // Start with a base value
  
  // Add value based on property size and characteristics
  if (data.propertySize) value += data.propertySize * 100;
  if (data.lotSize) value += data.lotSize * 50;
  
  // Add value from revenue streams
  if (data.annualGallons) value += data.annualGallons * 0.5;
  if (data.storeRevenue) value += data.storeRevenue * 1.2;
  if (data.carWashRevenue) value += data.carWashRevenue * 1.5;
  if (data.foodServiceRevenue) value += data.foodServiceRevenue * 1.3;
  
  // Adjust for property condition
  const conditionFactor = getConditionFactor(data.propertyCondition || 'average');
  value *= conditionFactor;
  
  // Adjust for building types
  if (data.assets?.Kiosk) value += 50000;
  if (data.assets?.Food_Booth) value += 75000;
  
  return Math.round(value);
}

/**
 * Helper function to calculate confidence score based on data completeness
 */
function calculateConfidenceScore(data: ValuationData): number {
  // Start with a base confidence
  let score = 0.7;
  
  // Add confidence for each important field that's filled in
  if (data.annualGallons) score += 0.03;
  if (data.storeRevenue) score += 0.03;
  if (data.propertySize) score += 0.02;
  if (data.lotSize) score += 0.02;
  if (data.yearBuilt) score += 0.02;
  if (data.trafficCount) score += 0.03;
  if (data.operatingExpenses) score += 0.03;
  if (data.fuelTypes?.length > 0) score += 0.02;
  
  // Cap the score at 0.95
  return Math.min(0.95, score);
}

/**
 * Helper function to determine property condition factor
 */
function getConditionFactor(condition: string): number {
  switch (condition?.toLowerCase()) {
    case 'excellent': return 1.2;
    case 'good': return 1.1;
    case 'average': return 1.0;
    case 'fair': return 0.9;
    case 'poor': return 0.8;
    default: return 1.0;
  }
}

/**
 * Creates a minimal ValuationData object from a database record
 * when full data is not available
 */
function createMinimalValuationData(record: any): ValuationData {
  return {
    location: '',
    propertyType: '',
    ownership: 'owned',
    leaseTermYears: 0,
    brandAffiliation: 'unbranded',
    fuelSupplyYears: 0,
    tankCapacity: 0,
    operatingHours: '',
    numberOfPumps: 0,
    fuelTypes: [],
    annualGallons: 0,
    fuelMargin: 0,
    propertySize: record.factors?.site_size as number || 0,
    canopySize: 0,
    lotSize: record.factors?.lot_size as number || 0,
    yearBuilt: record.factors?.year_built as number || new Date().getFullYear(),
    zoningType: '',
    propertyCondition: 'average',
    assets: {},
    hasCarWash: false,
    hasLeasedQSR: false, // Default to no QSR
    hasFoodService: false,
    foodServiceType: [],
    foodServiceOwnership: 'owned',
    foodServiceLeaseYears: 0,
    foodServiceRent: 0,
    additionalServices: [],
    storeRevenue: 0,
    storeMargin: 0,
    carWashRevenue: 0,
    carWashMargin: 0,
    totalGrossProfit: 0,
    utilities: 0,
    salaries: 0,
    insurance: 0,
    repairsAndMaintenance: 0,
    propertyTax: 0,
    advertisingAndPromotion: 0,
    merchantFees: 0,
    generalAndAdmin: 0,
    operatingExpenses: 0,
    trafficCount: record.factors?.traffic_count as number || 0,
    isAnnual: true
  };
}

/**
 * Helper function to normalize brand affiliation for database constraint
 */
function normalizeBrandAffiliation(brandAffiliation: string): string {
  const validValues = ['shell', 'exxon', 'bp', 'chevron', 'mobil', 'citgo', 'arco', 'unbranded', 'other'];
  
  // Convert to lowercase for comparison
  const normalized = brandAffiliation.toLowerCase();
  
  // Handle common mappings
  if (normalized === 'branded') {
    return 'other'; // Default for branded stations without specific brand
  }
  
  // Check if it's a valid value
  if (validValues.includes(normalized)) {
    return normalized;
  }
  
  // Default fallback
  return 'unbranded';
}
